/**
 * Шкаф из конструктора: цвета, сетка ячеек и разбор на детали.
 *
 * `wardrobeParts` раскладывает проект шкафа на детали — стенки, полки, перегородки,
 * штанги, фасады и ручки — в метрах, центром шкафа в начале координат, перед — к +Z.
 * Из них 3D-модель в планировщике собирается простыми мешами, без three.js в каталоге.
 * Двери в планировщике закрыты: открывать их — дело конструктора.
 */
import {
	WardrobeDoor,
	WardrobeDoorColor,
	WardrobeDoorDirection,
	WardrobeFill,
	WardrobeFinish,
	WardrobeLayout,
	WardrobeSide,
} from "@/api";
import { FURNITURE_BODY_COLORS } from "./colors";
import { WardrobePartMaterial, WardrobePartShape } from "./types";
import type { WardrobeCellGroup, WardrobeDesign, WardrobeRunId, WardrobeSettings } from "./types";

export const WARDROBE_FINISHES: Record<Exclude<WardrobeFinish, WardrobeFinish.Custom>, string> = {
	[WardrobeFinish.LightOak]: FURNITURE_BODY_COLORS.Дуб,
	[WardrobeFinish.DarkWalnut]: FURNITURE_BODY_COLORS.Орех,
	[WardrobeFinish.Graphite]: FURNITURE_BODY_COLORS.Графит,
	[WardrobeFinish.White]: FURNITURE_BODY_COLORS.Белый,
	[WardrobeFinish.WoodTexture]: "#b58d64",
};

export const WARDROBE_FINISH_LABELS: Record<
	Exclude<WardrobeFinish, WardrobeFinish.Custom>,
	string
> = {
	[WardrobeFinish.LightOak]: "Светлый дуб",
	[WardrobeFinish.DarkWalnut]: "Тёмный орех",
	[WardrobeFinish.Graphite]: "Графит",
	[WardrobeFinish.White]: "Белый",
	[WardrobeFinish.WoodTexture]: "Текстура дерева",
};

export const WARDROBE_WOOD_TEXTURE_URL = "/wardrobe-wood.webp";

export const WARDROBE_RUN_LABELS: Record<WardrobeRunId, string> = {
	back: "Задняя стена",
	left: "Левое крыло",
	right: "Правое крыло",
};

export const wardrobeFinishColor = (settings: Pick<WardrobeSettings, "finish" | "customColor">) =>
	settings.finish === WardrobeFinish.Custom
		? (settings.customColor ?? "#8098a0")
		: WARDROBE_FINISHES[settings.finish];

/** Ключ ячейки: у задней стороны — «колонка:ряд», у боковых крыльев — «сторона:колонка:ряд» */
export const wardrobeCellId = (column: number, row: number, run: WardrobeRunId = "back") =>
	run === "back" ? `${column}:${row}` : `${run}:${column}:${row}`;

/** Разобрать ключ ячейки на сторону, колонку и ряд */
export const parseWardrobeCellId = (
	id: string,
): { run: WardrobeRunId; column: number; row: number } => {
	const parts = id.split(":");
	if (parts.length === 3) {
		return {
			run: parts[0] as WardrobeRunId,
			column: Number(parts[1]),
			row: Number(parts[2]),
		};
	}
	return { run: "back", column: Number(parts[0]), row: Number(parts[1]) };
};

/** Какие стороны есть у шкафа этой формы (слева направо по обходу стен) */
export const wardrobeRunIds = (settings: WardrobeSettings): WardrobeRunId[] => {
	const layout = settings.layout ?? WardrobeLayout.Straight;
	if (layout === WardrobeLayout.Straight) return ["back"];
	if (layout === WardrobeLayout.Corner) {
		return (settings.cornerSide ?? WardrobeSide.Left) === WardrobeSide.Left
			? ["left", "back"]
			: ["back", "right"];
	}
	return ["left", "back", "right"];
};

/** Полная длина стороны от угла стены, мм */
export const wardrobeRunTotalLength = (settings: WardrobeSettings, run: WardrobeRunId) =>
	run === "back"
		? settings.width
		: run === "left"
			? (settings.leftLength ?? 1600)
			: (settings.rightLength ?? 1600);

/** Полезная ширина корпуса стороны, мм: боковые крылья начинаются после глубины заднего шкафа, а в самом углу у заднего шкафа нет боковины и перегородок */
export const wardrobeRunWidth = (settings: WardrobeSettings, run: WardrobeRunId) =>
	run === "back"
		? settings.width
		: Math.max(300, wardrobeRunTotalLength(settings, run) - settings.depth);

/** Число секций (колонок) на стороне */
export const wardrobeRunColumns = (settings: WardrobeSettings, run: WardrobeRunId) =>
	run === "back"
		? settings.columns
		: run === "left"
			? (settings.leftColumns ?? 2)
			: (settings.rightColumns ?? 2);

/** Габарит шкафа на плане: ширина вдоль задней стены, полная глубина с крыльями, высота, мм */
export const wardrobeSize = (settings: WardrobeSettings) => {
	const runs = wardrobeRunIds(settings);
	const depth = Math.max(
		settings.depth,
		...(runs.includes("left") ? [wardrobeRunTotalLength(settings, "left")] : []),
		...(runs.includes("right") ? [wardrobeRunTotalLength(settings, "right")] : []),
	);
	return { width: settings.width, depth, height: settings.height };
};

/**
 * Система координат стороны шкафа: `u` — вдоль стены (от угла комнаты, мм),
 * `v` — от стены в комнату (от 0 до глубины), мм. Возвращает точку плана (x, z) от центра габарита.
 */
export const wardrobeRunFrame = (settings: WardrobeSettings, run: WardrobeRunId) => {
	const { width, depth } = wardrobeSize(settings);
	const x0 = -width / 2;
	const z0 = -depth / 2;
	switch (run) {
		case "back":
			return (u: number, v: number) => [x0 + u, z0 + v] as const;
		case "left":
			return (u: number, v: number) => [x0 + v, z0 + u] as const;
		case "right":
			return (u: number, v: number) => [-x0 - v, z0 + u] as const;
	}
};

/** Границы объединения ячеек по колонкам и рядам */
export const wardrobeGroupBounds = (group: WardrobeCellGroup) => {
	const parsed = group.cells.map(parseWardrobeCellId);
	const columns = parsed.map((p) => p.column);
	const rows = parsed.map((p) => p.row);
	return {
		run: parsed[0]?.run ?? "back",
		minColumn: Math.min(...columns),
		maxColumn: Math.max(...columns),
		minRow: Math.min(...rows),
		maxRow: Math.max(...rows),
	};
};

/** Цвет фасадов: под дерево — как корпус, белый или свой */
export const wardrobeDoorColor = (
	design: Pick<WardrobeDesign, "finish" | "customColor" | "doorColor" | "doorCustomColor">,
) =>
	design.doorColor === WardrobeDoorColor.Wood
		? wardrobeFinishColor(design)
		: design.doorColor === WardrobeDoorColor.White
			? "#ece9e3"
			: design.doorCustomColor;

/** Деталь шкафа: брусок или цилиндр (штанга), размеры и центр — в метрах */
export interface WardrobePart {
	shape: WardrobePartShape;
	position: [number, number, number];
	/** Брусок: ширина, высота, глубина; штанга: длина, радиус, — */
	size: [number, number, number];
	/** Для боковых крыльев штанга поворачивается вдоль Z */
	rotation?: [number, number, number];
	material: WardrobePartMaterial;
}

const MM = 0.001;
const BOARD = 0.035;
const DOOR_GAP = 0.012;
const DOOR_OFFSET = 0.01;
const DOOR_THICKNESS = BOARD * 0.55;
const RAIL_RADIUS = 0.012;
const RAIL_DEPTH_OFFSET = 0.06;
const RAIL_HEIGHT_RATIO = 0.2;

/**
 * Геометрия колонок стороны шкафа, мм (от 0 до ширины корпуса стороны).
 * У заднего шкафа с боковым крылом крайняя угловая секция имеет ширину глубины шкафа `depth`
 * (на уровне дверей бокового крыла), а перегородка ставится ровно по линии крыла, чтобы
 * соседняя дверь закрывала секцию до самого угла.
 */
export const wardrobeColumnSpans = (settings: WardrobeSettings, run: WardrobeRunId) => {
	const board = BOARD / MM;
	const width = wardrobeRunWidth(settings, run);
	const columns = wardrobeRunColumns(settings, run);
	const depth = settings.depth;
	const runs = wardrobeRunIds(settings);
	const cornerLeft = run === "back" && runs.includes("left") && columns > 1;
	const cornerRight = run === "back" && runs.includes("right") && columns > 1;

	const leftInner = cornerLeft ? depth - board / 2 : board;
	const rightInner = cornerRight ? width - depth + board / 2 : width - board;
	const startCol = cornerLeft ? 1 : 0;
	const endCol = cornerRight ? columns - 2 : columns - 1;
	const normalCount = Math.max(0, endCol - startCol + 1);
	const normalWidth =
		normalCount > 0 ? (rightInner - leftInner - board * (normalCount - 1)) / normalCount : 0;

	const spans: { start: number; width: number }[] = [];
	for (let col = 0; col < columns; col++) {
		if (cornerLeft && col === 0) {
			spans.push({ start: board, width: Math.max(board, depth - board * 1.5) });
		} else if (cornerRight && col === columns - 1) {
			const s = width - depth + board / 2;
			spans.push({ start: s, width: Math.max(board, width - board - s) });
		} else {
			const idx = col - startCol;
			spans.push({ start: leftInner + idx * (normalWidth + board), width: normalWidth });
		}
	}
	return spans;
};

/**
 * Сетка стороны шкафа для размеров, мм: границы колонок от начала корпуса стороны и рядов от низа —
 * края корпуса и середины перегородок.
 */
export const wardrobeGridLines = (settings: WardrobeSettings, run: WardrobeRunId = "back") => {
	const board = BOARD / MM;
	const width = wardrobeRunWidth(settings, run);
	const spans = wardrobeColumnSpans(settings, run);
	const columns = [
		0,
		...spans.slice(0, -1).map((span) => span.start + span.width + board / 2),
		width,
	];
	const rowCount = settings.rows;
	const cellH = (settings.height - board * (rowCount + 1)) / rowCount;
	const rows = [
		0,
		...Array.from({ length: rowCount - 1 }, (_, i) => board * (i + 1.5) + cellH * (i + 1)),
		settings.height,
	];
	return { columns, rows };
};

/** Детали одной стороны в её локальных координатах (центр стороны в 0,0,0, перед к +Z) */
const buildRunLocalParts = (design: WardrobeDesign, run: WardrobeRunId): WardrobePart[] => {
	const width = wardrobeRunWidth(design, run) * MM;
	const height = design.height * MM;
	const depth = design.depth * MM;
	const columns = wardrobeRunColumns(design, run);
	const { rows } = design;
	const runs = wardrobeRunIds(design);
	const hasLeft = runs.includes("left");
	const hasRight = runs.includes("right");
	const colSpans = wardrobeColumnSpans(design, run).map((s) => ({
		start: s.start * MM,
		width: s.width * MM,
	}));
	const colLeft = (col: number) => -width / 2 + colSpans[col].start;
	const colRight = (col: number) => -width / 2 + colSpans[col].start + colSpans[col].width;
	const cellHeight = (height - BOARD * (rows + 1)) / rows;
	const runGroups = design.groups.filter((g) => wardrobeGroupBounds(g).run === run);
	const groupFor = (id: string) => runGroups.find((group) => group.cells.includes(id));
	const spans = (divider: number, cell: number, axis: "column" | "row") =>
		runGroups.some((group) => {
			const b = wardrobeGroupBounds(group);
			return axis === "column"
				? divider > b.minColumn && divider <= b.maxColumn && cell >= b.minRow && cell <= b.maxRow
				: divider > b.minRow && divider <= b.maxRow && cell >= b.minColumn && cell <= b.maxColumn;
		});
	const parts: WardrobePart[] = [];
	const body = (position: WardrobePart["position"], size: WardrobePart["size"]) =>
		parts.push({
			shape: WardrobePartShape.Box,
			position,
			size,
			material: WardrobePartMaterial.Body,
		});

	// Корпус: внешние боковины у свободных торцов (в углу между задним шкафом и крылом глухой боковины нет —
	// вместо неё глухая стенка идёт вдоль стены комнаты до угла, а крыло продолжает её без ступеньки)
	const skipLeftSide = run === "right" || (run === "back" && hasLeft);
	const skipRightSide = run === "left" || (run === "back" && hasRight);
	if (!skipLeftSide) {
		body([-width / 2 + BOARD / 2, 0, 0], [BOARD, height, depth]);
	}
	if (!skipRightSide) {
		body([width / 2 - BOARD / 2, 0, 0], [BOARD, height, depth]);
	}
	// Спинка: у бокового крыла продлевается на глубину заднего шкафа до самого угла комнаты
	if (run === "left") {
		body([depth / 2, 0, -depth / 2 + BOARD / 2], [width + depth, height, BOARD]);
	} else if (run === "right") {
		body([-depth / 2, 0, -depth / 2 + BOARD / 2], [width + depth, height, BOARD]);
	} else {
		body([0, 0, -depth / 2 + BOARD / 2], [width, height, BOARD]);
	}
	for (const y of [-height / 2 + BOARD / 2, height / 2 - BOARD / 2])
		body([0, y, 0], [width, BOARD, depth]);

	// Вертикальные перегородки — на стыке с углом перегородка стоит ровно вровень с крылом
	for (let index = 0; index < columns - 1; index++) {
		const vx = colRight(index) + BOARD / 2;
		for (let row = 0; row < rows; row++) {
			if (spans(index + 1, row, "column")) continue;
			body(
				[vx, -height / 2 + BOARD + cellHeight * (row + 0.5) + BOARD * row, 0],
				[BOARD, cellHeight + BOARD, depth],
			);
		}
	}
	// Горизонтальные полки между рядами — в угловых секциях доходят вплотную до спинки и крыла без отступа
	for (let index = 0; index < rows - 1; index++) {
		for (let column = 0; column < columns; column++) {
			if (spans(index + 1, column, "row")) continue;
			const extendLeft =
				(run === "back" && hasLeft && column === 0) || (run === "right" && column === 0);
			const extendRight =
				(run === "back" && hasRight && column === columns - 1) ||
				(run === "left" && column === columns - 1);
			const cW = colSpans[column].width;
			const baseX = (colLeft(column) + colRight(column)) / 2;
			const shelfW = cW + BOARD + (extendLeft ? BOARD / 2 : 0) + (extendRight ? BOARD / 2 : 0);
			const shelfX = baseX - (extendLeft ? BOARD / 4 : 0) + (extendRight ? BOARD / 4 : 0);
			body(
				[shelfX, -height / 2 + BOARD + cellHeight * (index + 1) + BOARD * index, 0],
				[shelfW, BOARD, depth],
			);
		}
	}

	// Наполнение и фасады — по ячейке или по объединению, у его левой нижней ячейки
	for (let column = 0; column < columns; column++) {
		for (let row = 0; row < rows; row++) {
			const id = wardrobeCellId(column, row, run);
			const group = groupFor(id);
			const bounds = group ? wardrobeGroupBounds(group) : undefined;
			if (bounds && (column !== bounds.minColumn || row !== bounds.minRow)) continue;
			const minCol = bounds ? bounds.minColumn : column;
			const maxCol = bounds ? bounds.maxColumn : column;
			const isCornerHidden =
				run === "back" && ((hasLeft && minCol === 0) || (hasRight && maxCol === columns - 1));
			const groupRows = bounds ? bounds.maxRow - bounds.minRow + 1 : 1;
			const leftEdge = colLeft(minCol);
			const rightEdge = colRight(maxCol);
			const groupWidth = rightEdge - leftEdge;
			const groupHeight = cellHeight * groupRows + BOARD * (groupRows - 1);
			const x = (leftEdge + rightEdge) / 2;
			const y = bounds
				? -height / 2 + BOARD + groupHeight / 2 + bounds.minRow * (cellHeight + BOARD)
				: -height / 2 + BOARD + cellHeight * (row + 0.5) + BOARD * row;
			const cell = design.cells[id];

			if (cell?.fill === WardrobeFill.Rail) {
				parts.push({
					shape: WardrobePartShape.Rod,
					position: [x, y + groupHeight * RAIL_HEIGHT_RATIO, RAIL_DEPTH_OFFSET],
					size: [groupWidth, RAIL_RADIUS, 0],
					rotation: [0, 0, Math.PI / 2],
					material: WardrobePartMaterial.Metal,
				});
			}
			const shelves = cell?.shelves ?? 0;
			for (let shelf = 1; shelf < shelves; shelf++) {
				body(
					[x, y - groupHeight / 2 + (groupHeight * shelf) / shelves, 0],
					[groupWidth, BOARD, depth],
				);
			}

			const door =
				cell?.door ?? (group ? WardrobeDoor.Door : (design.defaultDoor ?? WardrobeDoor.None));
			if (isCornerHidden || !design.doorsVisible || door !== WardrobeDoor.Door) continue;
			const direction = cell?.direction ?? design.defaultDirection ?? WardrobeDoorDirection.Left;
			const doorWidth = groupWidth + BOARD - DOOR_GAP;
			const doorHeight = groupHeight + BOARD - DOOR_GAP;
			const front = depth / 2 + DOOR_OFFSET;
			parts.push({
				shape: WardrobePartShape.Box,
				position: [x, y, front],
				size: [doorWidth, doorHeight, DOOR_THICKNESS],
				material: WardrobePartMaterial.Door,
			});
			// Ручка: у ящика — посередине, у двери — у края, противоположного петлям
			const handleX =
				direction === WardrobeDoorDirection.Drawer || direction === WardrobeDoorDirection.Bottom
					? x
					: direction === WardrobeDoorDirection.Left
						? x - doorWidth / 2 + doorWidth * 0.84
						: x + doorWidth / 2 - doorWidth * 0.84;
			parts.push({
				shape: WardrobePartShape.Box,
				position: [handleX, y, front + BOARD * 0.5],
				size: [0.024, doorHeight * 0.18, 0.03],
				material: WardrobePartMaterial.Handle,
			});
		}
	}
	return parts;
};

/** Положение и поворот крыла шкафа в метрах относительно центра всего габарита */
export const wardrobeRunTransform = (
	settings: WardrobeSettings,
	run: WardrobeRunId,
): { position: [number, number, number]; rotationY: number } => {
	const total = wardrobeSize(settings);
	const runW = wardrobeRunWidth(settings, run);
	const D = settings.depth;
	if (run === "back") {
		return {
			position: [0, 0, (-total.depth / 2 + D / 2) * MM],
			rotationY: 0,
		};
	}
	if (run === "left") {
		// Левое крыло: спинка к левой стене (-total.width / 2), фасад смотрит вправо (+X).
		// Поворот вокруг Y на +90° переводит локальный +Z в +X, а локальный +X в -Z (от переда к углу).
		return {
			position: [(-total.width / 2 + D / 2) * MM, 0, (-total.depth / 2 + D + runW / 2) * MM],
			rotationY: Math.PI / 2,
		};
	}
	// Правое крыло: спинка к правой стене (+total.width / 2), фасад смотрит влево (-X).
	// Поворот вокруг Y на -90° переводит локальный +Z в -X, а локальный +X в +Z (от угла к переду).
	return {
		position: [(total.width / 2 - D / 2) * MM, 0, (-total.depth / 2 + D + runW / 2) * MM],
		rotationY: -Math.PI / 2,
	};
};

/** Детали шкафа — та же раскладка, что в предпросмотре конструктора, с закрытыми дверями */
export const wardrobeParts = (design: WardrobeDesign): WardrobePart[] => {
	const parts: WardrobePart[] = [];
	for (const run of wardrobeRunIds(design)) {
		const localParts = buildRunLocalParts(design, run);
		const {
			position: [tx, ty, tz],
			rotationY,
		} = wardrobeRunTransform(design, run);
		for (const part of localParts) {
			const [lx, ly, lz] = part.position;
			if (run === "back") {
				parts.push({
					...part,
					position: [lx + tx, ly + ty, lz + tz],
				});
			} else {
				const sign = rotationY > 0 ? 1 : -1;
				// При повороте на ±90° вокруг Y: X_world = sign * lz, Z_world = -sign * lx
				const wx = tx + sign * lz;
				const wz = tz - sign * lx;
				if (part.shape === WardrobePartShape.Rod) {
					// Штанга вдоль локального X становится вдоль мирового Z
					parts.push({
						...part,
						position: [wx, ly + ty, wz],
						rotation: [Math.PI / 2, 0, 0],
					});
				} else {
					parts.push({
						...part,
						position: [wx, ly + ty, wz],
						size: [part.size[2], part.size[1], part.size[0]],
					});
				}
			}
		}
	}
	return parts;
};

/** Чертёж стороны шкафа на плане: контур корпуса, мм от центра габарита шкафа */
export interface WardrobePlanRun {
	run: WardrobeRunId;
	outline: (readonly [number, number])[];
	frontLine: readonly [readonly [number, number], readonly [number, number]];
	dividers: (readonly [readonly [number, number], readonly [number, number]])[];
}

export const wardrobePlan = (settings: WardrobeSettings): WardrobePlanRun[] => {
	const runs = wardrobeRunIds(settings);
	return runs.map((run) => {
		const frame = wardrobeRunFrame(settings, run);
		const runW = wardrobeRunWidth(settings, run);
		const start = run === "back" ? 0 : settings.depth;
		const end = start + runW;
		const D = settings.depth;
		const doorLineDepth = Math.max(40, D - 60);
		const { columns } = wardrobeGridLines(settings, run);
		const dividers = columns
			.slice(1, -1)
			.map((u) => [frame(start + u, 8), frame(start + u, D - 8)] as const);
		return {
			run,
			outline: [frame(start, 0), frame(end, 0), frame(end, D), frame(start, D)],
			frontLine: [frame(start + 8, doorLineDepth), frame(end - 8, doorLineDepth)] as const,
			dividers,
		};
	});
};
